import type {
    SlateBlock,
    TableCellBlock,
    TableRowBlock,
    TableBlock,
    ListItemBlock,
    CalloutBlock,
    EmbedBlock,
} from '@atlas/renderer';
import type { CodeBlockLanguage } from '@atlas/renderer/code-block';
import extractNodeTextRecursively from '@atlas/tool/box/slate/extractNodeTextRecursively';
import extractVideoEmbedBlockFromIframeElement from '@atlas/tool/box/slate/extractVideoEmbedBlockFromIframeElement';
import isHtmlElementNode from '@atlas/tool/box/dom/isHtmlElementNode';
import type { ListBlock } from '@atlas/tool/box/slate/isListBlock';
import extractVideoNodeSources from '@atlas/tool/box/slate/extractVideoNodeSources';
import isListHtmlElement from '@atlas/tool/box/dom/isListHtmlElement';

export default function extractBlockFromAtlasElement(element: HTMLElement): SlateBlock | null {
    const { classList } = element;
    if (!classList.contains('atlas-block')) return null;

    if (classList.contains('atlas-block-horizontal-line')) {
        return { type: 'horizontal-line', children: [{ text: '' }] };
    }

    if (classList.contains('atlas-block-paragraph')) {
        const children = extractNodeTextRecursively(element.childNodes);
        if (!children.length) return null;
        return {
            type: 'paragraph',
            children,
        };
    }

    if (classList.contains('atlas-block-heading-one')) {
        const children = extractNodeTextRecursively(element.childNodes);
        if (!children.length) return null;
        return {
            type: 'heading-one',
            children,
        };
    }

    if (classList.contains('atlas-block-heading-two')) {
        const children = extractNodeTextRecursively(element.childNodes);
        if (!children.length) return null;
        return {
            type: 'heading-two',
            children,
        };
    }

    if (classList.contains('atlas-block-heading-three')) {
        const children = extractNodeTextRecursively(element.childNodes);
        if (!children.length) return null;
        return {
            type: 'heading-three',
            children,
        };
    }

    if (classList.contains('atlas-block-quote')) {
        const children = extractNodeTextRecursively(element.childNodes);
        if (!children.length) return null;
        return {
            type: 'block-quote',
            children,
        };
    }

    if (classList.contains('atlas-block-code')) {
        const children = extractNodeTextRecursively(element.childNodes);
        if (!children.length) return null;
        const language =
            (element.className.match(/(^|\s)language-([^\s]+)(\s|$)/)?.[2] as CodeBlockLanguage) || undefined;
        return {
            type: 'code-block',
            children,
            language,
        };
    }

    if (classList.contains('atlas-block-callout')) {
        const textElement = element.querySelector<HTMLParagraphElement>('.atlas-block-paragraph');
        const textChildren = textElement && extractNodeTextRecursively(textElement.childNodes);
        const icon = element.querySelector<HTMLElement>('.atlas-block-icon')?.innerText || undefined;
        if (!textChildren?.length && !icon) return null;
        return {
            type: 'callout',
            children: [
                { type: 'icon', children: [{ text: '' }], icon },
                { type: 'paragraph', children: textChildren },
            ],
        } as CalloutBlock;
    }

    if (classList.contains('atlas-block-image')) {
        const imageElement = element.querySelector('img');
        if (!imageElement) return null;

        const align = classList.contains('atlas-block-image__centered')
            ? 'center'
            : classList.contains('atlas-block-image__float-left')
            ? 'left'
            : classList.contains('atlas-block-image__float-right')
            ? 'right'
            : 'justify';

        return {
            type: 'image',
            children: [{ text: '' }],
            src: imageElement.src,
            alt: imageElement.alt,
            width: parseInt(imageElement.getAttribute('width') || '') || undefined,
            align,
        };
    }

    if (classList.contains('atlas-block-video')) {
        const videoElement = element.querySelector('video');
        if (!videoElement) return null;

        const sources = extractVideoNodeSources(videoElement);
        if (!sources) return null;

        const align = classList.contains('atlas-block-image__centered')
            ? 'center'
            : classList.contains('atlas-block-image__float-left')
            ? 'left'
            : classList.contains('atlas-block-image__float-right')
            ? 'right'
            : 'justify';

        return {
            type: 'video',
            children: [{ text: '' }],
            alt: videoElement.title,
            align,
            sources: sources,
        };
    }

    if (classList.contains('atlas-block-video-embed')) {
        const iframeElement = element.querySelector('iframe');
        if (!iframeElement) return null;

        const videoEmbedBlock = extractVideoEmbedBlockFromIframeElement(iframeElement);
        if (!videoEmbedBlock) return null;

        videoEmbedBlock.align = classList.contains('atlas-block-video-embed__centered')
            ? 'center'
            : classList.contains('atlas-block-video-embed__float-left')
            ? 'left'
            : classList.contains('atlas-block-video-embed__float-right')
            ? 'right'
            : 'justify';

        return videoEmbedBlock;
    }

    if (classList.contains('atlas-block-embed')) {
        const iframeElement = element.querySelector<HTMLIFrameElement>('.atlas-block-embed-iframe');
        if (iframeElement) {
            if (iframeElement.tagName !== 'IFRAME') return null;

            const source = iframeElement.getAttribute('src');
            if (!source) return null;

            const height = (iframeElement.dataset.height && parseInt(iframeElement.dataset.height)) || undefined;

            const embedBlock: EmbedBlock = {
                type: 'embed',
                children: [{ text: '' }],
                source,
                height,
            };

            return embedBlock;
        }

        const htmlElement = element.querySelector<HTMLElement>('.atlas-block-embed-html');
        if (!htmlElement) return null;

        const source = htmlElement.dataset.source;
        if (!source) return null;

        const embedBlock: EmbedBlock = {
            type: 'embed',
            children: [{ text: '' }],
            source,
        };

        return embedBlock;
    }

    if (classList.contains('atlas-block-list')) {
        return extractAtlasListBlock(element as HTMLDivElement);
    }

    if (classList.contains('atlas-block-table')) {
        // The table element suppose to be inside, but there is hack that is fixing tags without <table> wrapper
        // that produces table with such a class name and it is handled by this check
        const tableElement = isHtmlElementNode(element, 'TABLE') ? element : element.querySelector('table');
        if (!tableElement) return null;

        if (!tableElement.rows.length) return null;

        const columnsCount = Math.max(...Array.from(tableElement.rows).map((row) => row.cells.length));
        if (!columnsCount) return null;

        const align = classList.contains('atlas-block-table__centered')
            ? 'center'
            : classList.contains('atlas-block-table__float-left')
            ? 'left'
            : classList.contains('atlas-block-table__float-right')
            ? 'right'
            : classList.contains('atlas-block-table__justify')
            ? 'justify'
            : undefined;

        const colgroup = Array.from(tableElement.children).find((el) => el.tagName === 'COLGROUP');

        const columns = colgroup?.children.length
            ? Array.from(colgroup.children).map((col) => ({
                  width: parseInt((col as HTMLTableColElement).width) || 0,
              }))
            : undefined;

        const tableBlock: TableBlock = {
            type: 'table',
            align,
            columns,
            children: Array.from(tableElement.rows).map<TableRowBlock>((row) => {
                const cellBlocks = Array.from(row.cells).map<TableCellBlock>((cell) => {
                    const align = cell.classList.contains('atlas-block-table-cell__align-justify')
                        ? 'justify'
                        : cell.classList.contains('atlas-block-table-cell__align-center')
                        ? 'center'
                        : cell.classList.contains('atlas-block-table-cell__align-right')
                        ? 'right'
                        : cell.classList.contains('atlas-block-table-cell__align-left')
                        ? 'left'
                        : undefined;

                    const cellContent = extractNodeTextRecursively(cell.childNodes);

                    return {
                        type: 'table-cell',
                        align,
                        children: cellContent.length ? cellContent : [{ text: '' }],
                    };
                });

                return {
                    type: 'table-row',
                    children: cellBlocks,
                };
            }),
        };

        return tableBlock;
    }

    return null;
}

// #region Helpers

function extractAtlasListBlock(element: HTMLDivElement): ListBlock {
    const start = element.querySelector<HTMLOListElement>('ol')?.start;
    const list: ListBlock = {
        type: element.classList.contains('atlas-block-list-ordered') ? 'numbered-list' : 'bulleted-list',
        ...(start && start > 1 && { start }),
        children: Array.from(element.children).reduce<Array<ListBlock | ListItemBlock>>((children, node) => {
            if (isListHtmlElement(node)) {
                return children.concat(
                    Array.from(node.children).reduce<ListItemBlock[]>(
                        (items, node) =>
                            isHtmlElementNode(node, 'LI')
                                ? items.concat({
                                      type: 'list-item',
                                      children: extractNodeTextRecursively(node.childNodes),
                                  })
                                : items,
                        [],
                    ),
                );
            }

            if (node.classList.contains('atlas-block-list')) {
                return children.concat(extractAtlasListBlock(node as HTMLDivElement));
            }

            return children;
        }, []),
    };

    return list;
}

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('extractBlockFromAtlasElement', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
